import React, { useState, useEffect } from 'react';
import SettingsLayout from './SettingsLayout';
import CompanyForm from '../Company/CompanyInfo/CompanyForm';
import ImageUpload from '../Company/CompanyInfo/ImageUpload';
import FoundingForm from '../Company/FoundingInfo/FoundingForm';
import SocialLinks from '../Company/Socials/SocialLinks';
import LocationForm from '../Company/Contact/LocationForm';
import axios from 'axios';
import { getApiUrl } from '@/config/api';
import { Card, CardContent } from '@/Components/ui/card';
import { Button } from '@/Components/ui/button';
import { Loader2, Check } from 'lucide-react';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^[0-9][0-9\s()-]{6,14}$/;

const normalizeUrl = (value) => {
  const trimmed = (value || '').trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const isValidUrl = (value) => {
  try {
    const parsed = new URL(normalizeUrl(value));
    return Boolean(parsed.hostname);
  } catch {
    return false;
  }
};

function CompanySettings() {
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Per-section save state — modern settings save each card independently
  // (GitHub/Stripe pattern), which maps 1:1 to our four save endpoints.
  const [savingSection, setSavingSection] = useState('');   // 'info'|'founding'|'socials'|'contact'
  const [savedSection, setSavedSection] = useState('');     // shows a transient ✓ on the button

  const [companyName, setCompanyName] = useState('');
  const [companyLink, setCompanyLink] = useState('');
  const [aboutUs, setAboutUs] = useState('');

  const [organizationType, setOrganizationType] = useState('');
  const [industryType, setIndustryType] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [yearOfEstablishment, setYearOfEstablishment] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [companyVision, setCompanyVision] = useState('');

  const [socialLinks, setSocialLinks] = useState([]);
  const [targetSchools, setTargetSchools] = useState([]);   // company's custom premium colleges
  const [newSchool, setNewSchool] = useState('');

  const [countryCode, setCountryCode] = useState('+91');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [mapLocation, setMapLocation] = useState('');
  const [companyLogo, setCompanyLogo] = useState('');
  const [companyBanner, setCompanyBanner] = useState('');

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      const companyId = localStorage.getItem('companyAccountId');
      if (!companyId) {
        window.location.href = '/hirewithus/login';
        return null;
      }

      const company_token = localStorage.getItem('company_token');
      if (!company_token) {
        window.location.href = '/hirewithus/login';
        return null;
      }

      setIsDataLoading(true);
      setErrorMessage('');
      
      try {
        const response = await axios.get(getApiUrl(`/api/hirewithus/settings/company/details/${companyId}`), {
          headers: { Authorization: `Bearer ${company_token}` },
          withCredentials: true,
        });

        const data = response.data;

        setCompanyName(data.companyName || '');
        setCompanyLogo(data.companyLogo || '');
        setCompanyBanner(data.companyBanner || '');
        setCompanyLink(data.companyLink || '');
        setAboutUs(data.aboutUs || '');

        setOrganizationType(data.organizationType || '');
        setIndustryType(data.industryType || '');
        setTeamSize(data.teamSize || '');
        setYearOfEstablishment(data.yearOfEstablishment || '');
        setCompanyWebsite(data.companyWebsite || '');
        setCompanyVision(data.companyVision || '');

        setSocialLinks(data.socialLinks || []);
        setTargetSchools(Array.isArray(data.targetSchools) ? data.targetSchools : []);

        setCountryCode(data.countryCode || '+91');
        setPhoneNumber(data.phoneNumber || '');
        setEmail(data.email || '');
        setMapLocation(data.mapLocation || '');
      } catch (error) {
        // 404 = new company with no profile yet — show empty form, not an error
        if (error.response?.status === 404) {
          /* empty profile is fine */
        } else {
          console.error('Error fetching company details:', error);
          setErrorMessage('Failed to load company details. Please refresh the page.');
        }
      } finally {
        setIsDataLoading(false);
      }
    };

    fetchCompanyDetails();
  }, []);

  // Client-side image guard — mirrors the server rules (type + 5 MB) so the
  // user gets an instant, friendly error instead of a round-trip 400/413.
  const ALLOWED_IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
  const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
  const validateImage = (file) => {
    if (!file) return 'No file selected.';
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) return 'Please choose a PNG, JPG, WEBP, or GIF image.';
    if (file.size > MAX_IMAGE_BYTES) return 'Image is too large — maximum size is 5 MB.';
    return '';
  };

  const uploadImage = async (file, kind) => {
    setErrorMessage('');
    setSuccessMessage('');
    const imgError = validateImage(file);
    if (imgError) { setErrorMessage(imgError); return; }

    const companyId = localStorage.getItem('companyAccountId');
    const token = localStorage.getItem('company_token');
    const formData = new FormData();
    formData.append(kind, file);
    try {
      const response = await axios.post(
        getApiUrl(`/api/hirewithus/company/upload-${kind}/${companyId}`),
        formData,
        { headers: { Authorization: `Bearer ${token || ''}` }, withCredentials: true }
      );
      const urlKey = `${kind}Url`;
      if (response.data?.[urlKey]) {
        const setter = kind === 'logo' ? setCompanyLogo : setCompanyBanner;
        setter(response.data[urlKey].split('/').pop());
        setSuccessMessage(`${kind.charAt(0).toUpperCase() + kind.slice(1)} uploaded successfully!`);
      }
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        setErrorMessage('Your session has expired. Please log in again.');
        setTimeout(() => { window.location.href = '/hirewithus/login'; }, 2000);
      } else {
        setErrorMessage(error.response?.data?.error || `Failed to upload ${kind}.`);
      }
    }
  };

  const handleLogoUpload = (file) => uploadImage(file, 'logo');
  const handleBannerUpload = (file) => uploadImage(file, 'banner');


  // ── Per-section validation ────────────────────────────────────────────
  const validators = {
    info: () => {
      if (!companyName.trim() || !companyLink.trim() || !aboutUs.trim())
        return 'Please fill in all company info fields.';
      if (companyName.trim().length < 2) return 'Company name must be at least 2 characters.';
      if (companyLink.trim().length < 2) return 'Company link must be at least 2 characters.';
      if (aboutUs.trim().length < 20) return 'About us must be at least 20 characters.';
      return '';
    },
    founding: () => {
      if (!organizationType || !industryType || !teamSize || !yearOfEstablishment ||
          !companyWebsite.trim() || !companyVision.trim())
        return 'Please fill in all founding info fields.';
      const currentYear = new Date().getFullYear();
      const year = Number(yearOfEstablishment);
      if (!Number.isInteger(year) || year < 1800 || year > currentYear)
        return `Year of establishment must be between 1800 and ${currentYear}.`;
      if (!isValidUrl(companyWebsite)) return 'Please enter a valid company website URL.';
      if (companyVision.trim().length < 20) return 'Company vision must be at least 20 characters.';
      return '';
    },
    socials: () => {
      const trimmed = socialLinks.map((l) => (l || '').trim()).filter(Boolean);
      if (trimmed.length === 0) return 'Please add at least one social media profile.';
      if (trimmed.some((l) => !isValidUrl(l))) return 'Please enter valid social media profile URLs.';
      return '';
    },
    contact: () => {
      if (!phoneNumber.trim() || !email.trim() || !mapLocation.trim())
        return 'Please fill in all contact info fields.';
      if (!PHONE_REGEX.test(phoneNumber.trim())) return 'Please enter a valid phone number.';
      if (!EMAIL_REGEX.test(email.trim().toLowerCase())) return 'Please enter a valid email address.';
      if (mapLocation.trim().length < 5) return 'Please enter a valid map location.';
      return '';
    },
    // Optional list — an empty list is valid (means "no custom target schools").
    targetSchools: () => '',
  };

  // ── Per-section save — each card saves independently (GitHub pattern) ──
  const saveSection = async (section) => {
    const companyId = localStorage.getItem('companyAccountId');
    const token = localStorage.getItem('company_token');
    if (!companyId) {
      setErrorMessage('Company ID not found. Please log in again.');
      return;
    }
    setErrorMessage('');
    setSuccessMessage('');
    const validationError = validators[section]();
    if (validationError) { setErrorMessage(validationError); return; }

    const cfg = { headers: { Authorization: `Bearer ${token || ''}` }, withCredentials: true };
    setSavingSection(section);
    try {
      if (section === 'info') {
        await axios.post(getApiUrl('/api/hirewithus/company/details'), {
          companyName: companyName.trim(), companyLink: companyLink.trim(),
          aboutUs: aboutUs.trim(), companyAccountId: companyId,
        }, cfg);
      } else if (section === 'founding') {
        await axios.post(getApiUrl('/api/hirewithus/company/founding'), {
          organizationType, industryType, teamSize, yearOfEstablishment,
          companyWebsite: normalizeUrl(companyWebsite),
          companyVision: companyVision.trim(), companyAccountId: companyId,
        }, cfg);
      } else if (section === 'socials') {
        await axios.post(getApiUrl('/api/hirewithus/company/socials'), {
          socialLinks: socialLinks.map((l) => {
            const t = (l || '').trim();
            return t ? normalizeUrl(t) : '';
          }),
          companyAccountId: companyId,
        }, cfg);
      } else if (section === 'contact') {
        await axios.put(getApiUrl('/api/hirewithus/company/contact'), {
          countryCode, phoneNumber: phoneNumber.trim(),
          email: email.trim().toLowerCase(), mapLocation: mapLocation.trim(),
          companyAccountId: companyId,
        }, cfg);
      } else if (section === 'targetSchools') {
        await axios.post(getApiUrl('/api/hirewithus/company/target-schools'), {
          targetSchools: targetSchools.map((s) => (s || '').trim()).filter(Boolean),
          companyAccountId: companyId,
        }, cfg);
      }
      setSavedSection(section);
      setTimeout(() => setSavedSection((s) => (s === section ? '' : s)), 2500);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        setErrorMessage('Your session has expired. Please log in again.');
        setTimeout(() => { window.location.href = '/hirewithus/login'; }, 2000);
      } else {
        setErrorMessage(error.response?.data?.error || error.message || 'An error occurred while saving.');
      }
    } finally {
      setSavingSection('');
    }
  };

  const addSchool = () => {
    const v = newSchool.trim();
    if (!v) return;
    setTargetSchools((prev) =>
      prev.some((s) => s.toLowerCase() === v.toLowerCase()) ? prev : [...prev, v]
    );
    setNewSchool('');
  };

  // A settings section card: header (title + description) + body + footer
  // with its own Save button that reflects saving / saved state.
  const SettingsCard = ({ section, title, subtitle, children, saveable = true }) => (
    <Card className="bg-white rounded-2xl shadow-sm border border-border w-full">
      <CardContent className="p-6 sm:p-8">
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-foreground">{title}</h2>
          {subtitle && <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>}
        </div>
        <div className="w-full">{children}</div>
        {saveable && (
          <div className="mt-8 pt-5 border-t border-border flex justify-end">
            <Button
              onClick={() => saveSection(section)}
              disabled={savingSection === section}
              className="gap-2 min-w-[120px]"
            >
              {savingSection === section ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>
              ) : savedSection === section ? (
                <><Check className="w-4 h-4" /> Saved</>
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );

  const SETTINGS_HEADER = {
    title: 'Settings',
    description: 'Manage your company profile, interview panel, and recruiter access.',
  };

  if (isDataLoading) {
    return (
      <SettingsLayout activeKey="company" {...SETTINGS_HEADER}>
        {[0, 1].map((i) => (
          <Card key={i} className="bg-white rounded-2xl shadow-sm border border-border w-full">
            <CardContent className="p-8">
              <div className="animate-pulse space-y-4">
                <div className="h-6 bg-muted rounded w-1/4"></div>
                <div className="h-24 bg-muted rounded"></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </SettingsLayout>
    );
  }

  return (
    <SettingsLayout activeKey="company" {...SETTINGS_HEADER}>
      {/* Global message banners (session expiry / load errors) */}
      {errorMessage && (
        <div className="p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
          {errorMessage}
        </div>
      )}
      {successMessage && (
        <div className="p-3 bg-green-500/10 text-green-700 rounded-lg text-sm">
          {successMessage}
        </div>
      )}

      {/* Logo & Banner — no explicit Save; uploads persist immediately. */}
      <SettingsCard
        title="Logo & Banner"
        subtitle="These appear on your public company page and job posts. Changes are saved as soon as you upload."
        saveable={false}
      >
        <ImageUpload
          onUpload={handleLogoUpload}
          companyLogo={companyLogo}
          onBannerUpload={handleBannerUpload}
          companyBanner={companyBanner}
        />
      </SettingsCard>

      <SettingsCard
        section="info"
        title="Company Information"
        subtitle="Your name, public profile link, and a short description."
      >
        <CompanyForm
          companyName={companyName} setCompanyName={setCompanyName}
          companyLink={companyLink} setCompanyLink={setCompanyLink}
          aboutUs={aboutUs} setAboutUs={setAboutUs}
        />
      </SettingsCard>

      <SettingsCard
        section="founding"
        title="Founding Information"
        subtitle="Details about your organization, industry, and size."
      >
        <FoundingForm
          organizationType={organizationType} setOrganizationType={setOrganizationType}
          industryType={industryType} setIndustryType={setIndustryType}
          teamSize={teamSize} setTeamSize={setTeamSize}
          yearOfEstablishment={yearOfEstablishment} setYearOfEstablishment={setYearOfEstablishment}
          companyWebsite={companyWebsite} setCompanyWebsite={setCompanyWebsite}
          companyVision={companyVision} setCompanyVision={setCompanyVision}
        />
      </SettingsCard>

      <SettingsCard
        section="socials"
        title="Social Media Profiles"
        subtitle="Links to your company's social presence."
      >
        <SocialLinks socialLinks={socialLinks} setSocialLinks={setSocialLinks} />
      </SettingsCard>

      <SettingsCard
        section="contact"
        title="Contact Information"
        subtitle="How candidates and recruiters can reach your company."
      >
        <LocationForm
          countryCode={countryCode} setCountryCode={setCountryCode}
          phoneNumber={phoneNumber} setPhoneNumber={setPhoneNumber}
          email={email} setEmail={setEmail}
          mapLocation={mapLocation} setMapLocation={setMapLocation}
        />
      </SettingsCard>

      <SettingsCard
        section="targetSchools"
        title="Target Schools"
        subtitle="Colleges your company treats as premium. These augment the platform tier list — matched candidates get a preferred badge and can be filtered on the applications page."
      >
        <div>
          <div className="flex flex-wrap gap-2">
            {targetSchools.length === 0 && (
              <span className="text-sm text-muted-foreground">
                No target schools yet. Add specific colleges below (e.g. “IIT Delhi”, “BITS Pilani”).
              </span>
            )}
            {targetSchools.map((s, idx) => (
              <span
                key={idx}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-3 py-1 text-sm text-foreground"
              >
                {s}
                <button
                  type="button"
                  onClick={() => setTargetSchools((prev) => prev.filter((_, i) => i !== idx))}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label={`Remove ${s}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <div className="mt-3 flex gap-2">
            <input
              value={newSchool}
              onChange={(e) => setNewSchool(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSchool(); } }}
              placeholder="Add a school (e.g. IIT Bombay)"
              className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
            />
            <button
              type="button"
              onClick={addSchool}
              className="rounded-lg bg-foreground px-4 py-2 text-sm font-medium text-background"
            >
              Add
            </button>
          </div>
        </div>
      </SettingsCard>
    </SettingsLayout>
  );
}

export default CompanySettings;
